import { useState, useEffect } from 'react'
import { FormTypes } from '@oneblink/types'
import { useAuth } from '@oneblink/apps-react'
import { authService } from '@oneblink/apps-react'
import { v4 } from 'uuid'

import DefaultForm from '../Form'
import { DataSubmission, getBeekeeperInfo } from '../../../lib/beekeeperInfo'

import LoadingSpinner from '../../../components/LoadingSpinner'
import ErrorMessage from '../../../components/ErrorMessage'
import useOneBlinkError from '../../../hooks/useOneBlinkError'
import { replaceElementOptions } from '../../../lib/definition-manipulation'

import { parse, format } from 'date-fns'
import { HTTPError } from '../../../lib/http'

type Props = { form: FormTypes.Form; preFillData: Record<string, unknown> }

type BeekeeperFormPrefill = {
  Declarer_name: string
  Declarer_email?: string
  Authorisation_number: number
  Registration_email: string
  anniversary_Date?: string
  company_Name: string
  isShowingDataSubmissionSelect__hidden: boolean
  dataSubmissions: DataSubmission[]
}

const formatBeekeeperDate = (dateString: string, formatString: string) => {
  return format(
    parse(dateString, 'd/MM/yyyy h:mm:ss a', new Date()),
    formatString,
  )
}

export default function BeekeeperForm({ form, preFillData }: Props) {
  const [isFetching, setIsFetching] = useState(true)
  const [fetchError, setFetchError] = useOneBlinkError()
  const [beekeeperFormPrefill, setBeekeeperFormPrefill] =
    useState<BeekeeperFormPrefill>()

  const { userProfile } = useAuth()

  useEffect(() => {
    const fetchBeekeeperInfo = async () => {
      try {
        setIsFetching(true)
        const beekeeperInfo = await getBeekeeperInfo()

        setBeekeeperFormPrefill({
          Declarer_name: [beekeeperInfo.FirstName, beekeeperInfo.LastName]
            .filter(Boolean)
            .join(' '),
          Declarer_email: userProfile?.email,
          Authorisation_number: parseInt(beekeeperInfo.AuthorisationNumber),
          Registration_email: beekeeperInfo.CompanyEmail,
          anniversary_Date: beekeeperInfo.AnniversaryDate
            ? format(
                parse(beekeeperInfo.AnniversaryDate, 'dd/MM/yyyy', new Date()),
                'yyyy-MM-dd',
              )
            : undefined,
          company_Name: beekeeperInfo.CompanyName,
          isShowingDataSubmissionSelect__hidden:
            !!beekeeperInfo.Data_Submissions.length,
          dataSubmissions: beekeeperInfo.Data_Submissions.map((s) => ({
            ...s,
            Period_From: formatBeekeeperDate(s.Period_From, 'yyyy-MM-dd'),
            Period_To: formatBeekeeperDate(s.Period_To, 'yyyy-MM-dd'),
          })),
        })

        if (beekeeperInfo.Data_Submissions.length) {
          const opts = beekeeperInfo.Data_Submissions.map((dataSubmission) => {
            return {
              id: v4(),
              label: `${formatBeekeeperDate(
                dataSubmission.Period_From,
                'dd/MM/yyyy',
              )} - ${formatBeekeeperDate(
                dataSubmission.Period_To,
                'dd/MM/yyyy',
              )}`,
              value: dataSubmission.Data_Submission_Id,
            }
          })

          replaceElementOptions(form, 'Data_Submission_Id', opts)
        }
      } catch (e) {
        if (e instanceof HTTPError && e.status === 401) {
          setFetchError('Unauthorised')
          authService.logout()
        } else if (e instanceof HTTPError && e.status === 403) {
          setFetchError(
            'You have no beekeeper record associated with your user.',
          )
        } else {
          setFetchError('User information is incorrect.')
        }
      }

      setIsFetching(false)
    }

    fetchBeekeeperInfo()
  }, [setFetchError, userProfile, form])

  if (isFetching) {
    return <LoadingSpinner>Retrieving your details</LoadingSpinner>
  }

  if (fetchError) {
    return <ErrorMessage>{fetchError.message}</ErrorMessage>
  }

  return (
    <DefaultForm
      form={form}
      preFillData={{ ...preFillData, ...beekeeperFormPrefill }}
    />
  )
}
